import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, Plus, Edit, UserX } from "lucide-react";
import { format } from "date-fns";

export default function AdminAccounts() {
  const { toast } = useToast();
  const [admins, setAdmins] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<any>(null);
  const [currentUserId, setCurrentUserId] = useState<string>("");

  // Form state
  const [adminLevel, setAdminLevel] = useState<"super_admin" | "support_admin" | "template_admin">("support_admin");
  const [permissions, setPermissions] = useState({
    can_access_production: false,
    can_delete_content: false,
    can_manage_users: false,
    can_edit_templates: true,
    can_edit_ai_prompts: false,
    can_view_analytics: true,
  });
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    loadAdmins();
    loadCurrentUser();
  }, []);

  async function loadCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) setCurrentUserId(user.id);
  }

  async function loadAdmins() {
    try {
      // admin_users.user_id points at auth.users, not profiles, so a
      // PostgREST embed fails the whole request. Read profiles separately.
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const userIds = Array.from(
        new Set((data || []).map((a: any) => a.user_id).filter(Boolean)),
      );
      let profileById = new Map<string, any>();
      if (userIds.length > 0) {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', userIds);
        profileById = new Map(
          (profileRows || []).map((p: any) => [p.id, p]),
        );
      }

      setAdmins(
        (data || []).map((a: any) => ({
          ...a,
          profiles: profileById.get(a.user_id) || null,
        })),
      );
    } catch (error: any) {
      console.error('Error loading admins:', error);
      toast({
        title: "Error",
        description: "Failed to load admin accounts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleEditAdmin(admin: any) {
    setSelectedAdmin(admin);
    setAdminLevel(admin.admin_level);
    setPermissions({
      can_access_production: admin.can_access_production,
      can_delete_content: admin.can_delete_content,
      can_manage_users: admin.can_manage_users,
      can_edit_templates: admin.can_edit_templates,
      can_edit_ai_prompts: admin.can_edit_ai_prompts,
      can_view_analytics: admin.can_view_analytics,
    });
    setIsActive(admin.is_active);
    setEditModalOpen(true);
  }

  async function handleSaveAdmin() {
    try {
      const { error } = await supabase
        .from('admin_users')
        .update({
          admin_level: adminLevel,
          ...permissions,
          is_active: isActive,
        })
        .eq('id', selectedAdmin.id);

      if (error) throw error;

      toast({ title: "Admin Updated", description: "Changes saved successfully" });
      setEditModalOpen(false);
      loadAdmins();
    } catch (error: any) {
      console.error('Update error:', error);
      toast({
        title: "Error",
        description: "Failed to update admin",
        variant: "destructive",
      });
    }
  }

  async function handleDeactivate(adminId: string) {
    if (!confirm("Are you sure you want to deactivate this admin?")) return;

    try {
      const { error } = await supabase
        .from('admin_users')
        .update({ is_active: false, deactivated_at: new Date().toISOString() })
        .eq('id', adminId);

      if (error) throw error;

      toast({ title: "Admin Deactivated" });
      loadAdmins();
    } catch (error: any) {
      console.error('Deactivation error:', error);
      toast({
        title: "Error",
        description: "Failed to deactivate admin",
        variant: "destructive",
      });
    }
  }

  const filteredAdmins = admins.filter(admin =>
    admin.profiles?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    admin.profiles?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Admin Accounts</h1>
          <p className="text-muted-foreground">Manage administrator access and permissions</p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Admin (Coming Soon)
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search admins..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Admin Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead className="w-[150px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
              </TableRow>
            ) : filteredAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">No admins found</TableCell>
              </TableRow>
            ) : (
              filteredAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell className="font-medium">
                    {admin.profiles?.full_name || 'Unknown'}
                  </TableCell>
                  <TableCell>{admin.profiles?.email}</TableCell>
                  <TableCell>
                    <Badge variant={admin.admin_level === 'super_admin' ? 'default' : 'secondary'}>
                      {admin.admin_level}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={admin.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {admin.last_login_at ? format(new Date(admin.last_login_at), 'PP') : 'Never'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditAdmin(admin)}
                        disabled={admin.user_id === currentUserId}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeactivate(admin.id)}
                        disabled={admin.user_id === currentUserId || !admin.is_active}
                      >
                        <UserX className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Admin Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Admin Permissions</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Admin Level</Label>
              <Select value={adminLevel} onValueChange={(value) => setAdminLevel(value as typeof adminLevel)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="content_admin">Content Admin</SelectItem>
                  <SelectItem value="support_admin">Support Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label>Permissions</Label>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Access Production</span>
                <Switch
                  checked={permissions.can_access_production}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, can_access_production: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm">Delete Content</span>
                <Switch
                  checked={permissions.can_delete_content}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, can_delete_content: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm">Manage Users</span>
                <Switch
                  checked={permissions.can_manage_users}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, can_manage_users: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm">Edit Templates</span>
                <Switch
                  checked={permissions.can_edit_templates}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, can_edit_templates: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm">Edit AI Prompts</span>
                <Switch
                  checked={permissions.can_edit_ai_prompts}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, can_edit_ai_prompts: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm">View Analytics</span>
                <Switch
                  checked={permissions.can_view_analytics}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, can_view_analytics: checked })
                  }
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Active Status</Label>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAdmin}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
